import moment from "moment";
import JSON from "json5";
import { isNotEmptyObject } from "class-validator";
import * as unitsOfMeasure from "@signolteam/shared/units-of-measure";
import {
  COGNITO_ENABLED,
  IDP_LOGOUT_URL,
  COGNITO_APP_CLIENT_ID,
  COGNITO_REDIRECT_URI,
  COGNITO_TRUSTED_CLIENTS,
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  USER_COOKIE,
  ROLE_COOKIE,
  LOGIN_DATE_COOKIE,
  IMPERSONATED_SESSION_COOKIE,
  SAML_SSO_COOKIE,
  OperatorCodeAliasSettings,
  SESSION_ID_COOKIE,
  SELECTED_BEHAVIOUR,
  SIGNOL_STAGE,
} from "@src/constants/constants";
import { calculateCO2Convertion } from "@src/constants/convertions";
import StorageUtils from "./storageUtils";

export default class SignolUtils {
  static isProd() {
    return SIGNOL_STAGE === "production";
  }

  static normalizeBehiveKey(key) {
    const prefix = SignolUtils.isProd() ? "prod" : "dev";
    return key?.replace(/^(prod|dev)\//, `${prefix}/`);
  }

  static isDemo() {
    return SIGNOL_STAGE === "demo";
  }

  static isMobile() {
    // Mobile screen width usually are smaller than 768px
    return window.innerWidth < 768;
  }

  static isMobileLG() {
    // Match tailwind's lg breakpoint
    return window.innerWidth < 1024;
  }

  static isManager(role) {
    const currentRole = role || StorageUtils.getItem(ROLE_COOKIE);
    return currentRole === "FUEL_MANAGER";
  }

  static isAdmin(role) {
    const currentRole = role || StorageUtils.getItem(ROLE_COOKIE);
    return currentRole === "ADMINISTRATOR";
  }

  static getLoginSSO(email) {
    let trustedIdP = null;
    if (COGNITO_ENABLED === "true" && !!email) {
      const trustedProviders = JSON.parse(COGNITO_TRUSTED_CLIENTS);
      Object.keys(trustedProviders).forEach((domain) => {
        if (email.endsWith(domain)) {
          trustedIdP = trustedProviders[domain];
        }
      });
    }
    return trustedIdP;
  }

  static getCognitoClient() {
    let clientParams = null;
    if (COGNITO_ENABLED === "true") {
      clientParams = {
        clientId: COGNITO_APP_CLIENT_ID,
        redirectUri: `${COGNITO_REDIRECT_URI}?saml=true`,
      };
    }
    return clientParams;
  }

  static getCurrentRefreshToken() {
    return StorageUtils.getItem(REFRESH_TOKEN_COOKIE);
  }

  static getCurrentUsername() {
    const user = StorageUtils.getObjectItem(USER_COOKIE);
    return user ? user.username?.toLowerCase() : null;
  }

  static getCurrentOperatorCode() {
    const user = StorageUtils.getObjectItem(USER_COOKIE);
    return user ? user.metadata?.external_id : null;
  }

  static isCurrentSSOSession() {
    return StorageUtils.getItem(SAML_SSO_COOKIE) === "true";
  }

  static logoutCurrentSSO() {
    window.location.href = IDP_LOGOUT_URL;
  }

  static removeSessionCookies() {
    StorageUtils.removeItem(ACCESS_TOKEN_COOKIE);
    StorageUtils.removeItem(REFRESH_TOKEN_COOKIE);
    StorageUtils.removeItem(SAML_SSO_COOKIE);
    StorageUtils.removeItem(USER_COOKIE);
    StorageUtils.removeItem(ROLE_COOKIE);
    StorageUtils.removeItem(LOGIN_DATE_COOKIE);
    StorageUtils.removeItem(IMPERSONATED_SESSION_COOKIE);
    StorageUtils.removeItem(SESSION_ID_COOKIE);
    StorageUtils.removeItem(SELECTED_BEHAVIOUR);
    try {
      window?.hj?.("identify", null);
    } catch (e) {
      //do nothing
    }
  }

  static generateIndustrySignupLink(usernameType) {
    const requiredEmail = String(usernameType).toLowerCase() === "email";
    const signupLink = SignolUtils.generateUrlWithEncodedTokenParams(
      "/signup",
      {
        usernameType,
        requiredEmail,
        industry: requiredEmail ? "aviation" : "shipping",
        operatorCodeLabel: requiredEmail
          ? JSON.stringify(OperatorCodeAliasSettings.aviation)
          : JSON.stringify(OperatorCodeAliasSettings.shipping),
      },
    );
    return signupLink;
  }

  static generateUrlWithEncodedTokenParams(urlPath, signolTokenData) {
    const t = `SG-${btoa(JSON.stringify(signolTokenData))}`;
    const url = `${urlPath}?t=${t}`;
    return url;
  }

  static mergeUserMetadata(identityMetadata, extendedMetadata) {
    const metadata = { ...identityMetadata };
    const metadataTypes = {
      STRING: (val) => String(val),
      NUMBER: (val) => Number(val),
      BOOLEAN: (val) => String(val).toLowerCase() === "true", // Boolean(val) --> doesn't work as expected
      TIMESTAMP: (val) => new Date(val),
      JSON: (val) => JSON.parse(String(val)),
    };
    (extendedMetadata || []).forEach((m) => {
      const parser = metadataTypes[m.type];
      metadata[m.key] = parser(m.value);
      if (m.modifiedAt) {
        metadata[`${m.key}_modified_at`] = new Date(m.modifiedAt);
      }
    });
    return { metadata };
  }

  static async isMockedAuth({ username, password }) {
    const lowerUser = username.toLowerCase();
    const lowerPass = password.toLowerCase();
    async function digestMessage(message) {
      const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
      const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8); // hash the message
      const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(""); // convert bytes to hex string
      return hashHex;
    }

    function getHashForUser(username) {
      // Define the hash list
      const hashes = [
        "4964525bf10833e40198389bf594172a6f220c0c31153926d4ecc1370474b84d",
        "93b6c9b0f97a2a92c024ea61f333ebe25055e4b641d1a7b7883f5bc644dbb8f1",
        "fe45e8f22c3c6f21a932c81d896fb4fe1beedf4c2af86f108742b2fbc7130407",
        "87d433779683130b62c7dc510ee1c2fdbe621a7f53802ade433c46f0fc790de6",
        "2cfdb3ae705540c72b464674510889be323e58eaa00e4e93024aa77102cc8b81",
        "ac8039a0758f70871ae74d1911cec98a215ae4ddd554fb04500058c4fab8f2d7",
        "c9f876a1cf8610ad6defabc601897af309077d9eeb3f9226b12c5c2f1ccd5185",
        "ff87719660c6ea864979e39f9d3da50aea99ada1e2e468330ca155fd96c7fc94",
        "daa9840a4eec34aa22bf4a3456199aaa7d196cf2106d2844d0dd46f0c9733f77",
        "21547d90da695dfb0c51a24b13ee12109a4feaf2d190e0d13d1b0f97c6d9b39a",
        "8301fcbbb6c5f6115d0e78346988ac7f4ca1cd91eb5e4c72b7941174497a9061",
        "bfbd98660e79cf86c2816f88f9b95fc771d49e85af10961da766a203e64c12e3",
        "f65598f40528267fe9d20b7db7f9685a49274e534e92ab7dd6b7cda1ad9b56db",
        "81466d17d430fa5f3c6a6b0628f5ec60c6dd1709cc82ad31c83476adae7572d3",
        "901e151b3c55964420737800361eb20988ebe37c0c372c6024c9b963dab75b99",
        "718d23f07524dc896f82ecc5632da9f56eaeddfb8da61242017e9d97f761150d",
        "4a623c26464375bac5a63dd968ba28b8e613c8ebbb4ccfcc6aafe81f3927556f",
        "3bca17416e124e1b789b370df5e8478b964e8e88346fc62415360078bc2d707e",
        "b00afed3e9889295f0cea6859a0870aeb64791fd26322f75048a1136d0416eb4",
        "d3918d89c75a0fd9276cfcf9297a14a6bd9fb19c51253af33adf2cf36acd115b",
        "73b80067c66f6bd96cca1d2d52a671c308e537fccd7b03383d0c96698a43985d",
        "6c3493a017259a9311ff2d6859eb795ec3bfdd3ae12d26cb63504dcbf4252cc8",
        "211cbb665729dcc91f2f78171179a98c4ba4b1ecd970deedb9700faa12f336ba",
        "e27439bd7a1b82ad3c1dd16a67ba74b8b78635a5132efaf0d9db3f8789586050",
      ];
      // Regular expressions for username patterns
      const maritimeRegex = /^captainmaritimedemo([1-6])$/;
      const aviationRegex = /^captainaviationdemo([1-6])@sg\.io$/;
      // Match the username against the patterns
      let match = maritimeRegex.exec(username) || aviationRegex.exec(username);
      if (!match) {
        return false; // Return null if the username does not match any pattern
      }
      // Extract the number X from the username
      const userNumber = parseInt(match[1], 10);
      // Get the current date
      const now = new Date();
      const currentMonth = now.getMonth() + 1; // Months are 1-12
      const currentDay = now.getDate();
      // Determine the hash duration based on the user number and calculate the hash index based on the duration
      let hashIndex;
      if (userNumber === 1 || userNumber === 2) {
        // 2 months
        hashIndex = Math.floor((currentMonth - 1) / 2); // Group months in pairs
        if (userNumber % 2 === 0) {
          hashIndex++; // Even numbers have a 1-month offset
        }
      } else if (userNumber === 3 || userNumber === 4) {
        // 1 month
        hashIndex = (currentMonth - 1) % 12; // Monthly change
        if (userNumber % 2 === 0) {
          hashIndex =
            currentDay <= 15 ? (currentMonth - 1) % 12 : currentMonth % 12; // Even numbers have half-month offset
        }
      } else if (userNumber === 5 || userNumber === 6) {
        // Half a month
        hashIndex = Math.floor(
          ((currentMonth - 1) * 2 + (currentDay > 15 ? 1 : 0)) % 24,
        ); // Half-month
        if (userNumber % 2 === 0) {
          // Even numbers have a 1-week offset
          hashIndex = Math.floor(
            ((currentMonth - 1) * 2 +
              (currentDay < 7 ? 0 : currentDay < 21 ? 1 : 2)) %
              24,
          );
        }
      } else {
        return false; // Invalid number, should not happen with regex
      }
      // Return the corresponding hash
      return hashes[hashIndex];
    }

    function findDemoPassword(lowerUser, passwordHex) {
      // DEMO passwords: https://docs.google.com/spreadsheets/d/1ObMEOy5HmggUqKC6Zi9W5-va0gk4RQpEIhAPOOhOT8s/
      // vlad and chief are permanent internal accounts; all captaindemo accounts use time-based hashes only
      if (
        (lowerUser === "vlad@sg.io" &&
          passwordHex ===
            "f5640072e8a03c66da16f022bcf02e4a507cae54bfdac31fb9810e1887a6dbda") ||
        (lowerUser === "chief1234ard" &&
          passwordHex ===
            "a1826f931844c6111ebc97efba09da262a8f2b83ecb42be754a25c8278e32e15")
      ) {
        return true;
      }
      const usedHash = getHashForUser(lowerUser);
      // Compare the hash
      if (passwordHex === usedHash) {
        return true;
      }
      return false;
    }

    // Wait for promise to resolve
    const digestHex = await digestMessage(lowerPass);
    const digestedPassword = digestHex;
    return findDemoPassword(lowerUser, digestedPassword);
  }

  static getIndustry() {
    let user = StorageUtils.getObjectItem(USER_COOKIE);
    return user?.customer?.industry || user?.metadata?.industry;
  }

  static getPreferredEventName() {
    let user = StorageUtils.getObjectItem(USER_COOKIE);
    return user?.customer?.preferredEventName;
  }

  static getPreferredEventNamePlural() {
    let user = StorageUtils.getObjectItem(USER_COOKIE);
    return user?.customer?.preferredEventNamePlural;
  }

  static getStateIcon(state) {
    const stateIcon = {
      EXCLUSION: "ban",
      INCOMPLETE: "question",
      SUCCESS: "check",
      FAIL: "close",
    }[state];
    return stateIcon;
  }

  static getIndustryIcon(industryEventType, isActive = false) {
    const industryNormalized = (
      industryEventType || SignolUtils.getIndustry()
    )?.toUpperCase();
    switch (industryNormalized) {
      case "AVIATION":
      case "FLIGHT": {
        return isActive ? "planeFilled" : "planeOutline";
      }
      case "SHIPPING":
      case "VOYAGE": {
        return isActive ? "shipFilled" : "shipOutline";
      }
      default: {
        return null;
      }
    }
  }

  static formatGroupDate(periodKey) {
    return periodKey.length === 7
      ? `${periodKey.replace("-", "/")}/01` // If short (e.g., "2023-06"), add "/01"
      : periodKey.replace(/-/g, "/");
  }

  /**
   * Crew rotation data is normalised by the crew_rotations_job pipeline
   * (signol-automations/crew_rotations_job/crew_rotations_tool.py) before it is
   * snapshotted. Anything captured from a crew member in the app has to be put
   * through the same cleaning, otherwise the two sources produce different
   * values for the same person.
   *
   * Mirrors `_sanitize_cell_value`: newlines, carriage returns and tabs become
   * a single space, repeated spaces collapse, and the result is trimmed.
   */
  static sanitizeCrewValue(value) {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/[\r\n\t]+/g, " ")
      .replace(/ +/g, " ")
      .trim();
  }

  /** Crew names (and ship names) are stored uppercase by the pipeline. */
  static normalizeCrewName(value) {
    return SignolUtils.sanitizeCrewValue(value).toUpperCase();
  }

  /**
   * Operator codes (discharge book numbers) keep alphanumeric characters only —
   * mirrors the `[^A-Za-z0-9]` strip the pipeline applies to
   * `discharge_book_number`, so "XYZ-12345" and "XYZ 12345" both become
   * "XYZ12345". Rows that clean down to an empty string are dropped by the
   * pipeline, so an empty result here means the code is unusable.
   */
  static normalizeCrewOperatorCode(value) {
    return SignolUtils.sanitizeCrewValue(value).replace(/[^A-Za-z0-9]/g, "");
  }

  /**
   * Crew rotation dates are stored as YYYY-MM-DD; the pipeline drops rows whose
   * date cannot be parsed, so an unparseable value returns "".
   */
  static normalizeCrewDate(value) {
    const sanitized = SignolUtils.sanitizeCrewValue(value);
    if (!sanitized) return "";
    const parsed = moment(sanitized, ["YYYY-MM-DD", moment.ISO_8601]);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  }

  /** Converts event metadata to an object, and adds extra values not in the raw event data */
  static calculateEventDataComputedValues(event, customerPredictionsEnabled) {
    let e = { ...event };
    SignolUtils.toMetadataValues(e);
    SignolUtils.calculateEventBehaviourSuccessStates(
      e,
      customerPredictionsEnabled,
    );
    SignolUtils.calculateEventImpact(e);
    return e;
  }

  /** Used for Event Data, and this.props.authStore.loggedUser */
  static toMetadataValues = (obj) => {
    if (Array.isArray(obj.metadata)) {
      const currentMetadata = {};
      obj.metadata.forEach((m) => {
        currentMetadata[m.key] = m.value;
        if (m.modifiedAt) {
          // util on impersonated view for SIGNOL ADMIN
          currentMetadata[`${m.key}_modified_at`] = new Date(m.modifiedAt);
        }
      });
      obj.metadata = currentMetadata;
    }
  };

  static calculateEventImpact = (ev) => {
    if (!!ev.totalFuelSavedKg) {
      ev.savings = ev.totalFuelSavedKg;
    } else if (ev.totalFuelSaved) {
      ev.savings = ev.totalFuelSaved;
    } else {
      let savings = 0;
      ev.behaviours.forEach((bvr) => {
        savings += bvr.fuelSaved || 0;
      });
      savings = savings > 0 ? savings : 0;
      ev.savings = savings;
    }
  };

  /** Populates event.behaviour[]._success
   * @param {boolean} customerPredictionsEnabled
   */
  static calculateEventBehaviourSuccessStates = (
    ev,
    customerPredictionsEnabled,
  ) => {
    const behavioursWithSuccessObj = [];
    ev.behaviours.forEach((bvr) => {
      const isExclusion =
        bvr.reasons.find(
          (r) => r?.startsWith("NOT_POSSIBLE") || r == "Not Possible",
        ) != null;
      const isFaultyData =
        bvr.reasons.find(
          (r) => r?.startsWith("FAULTY_DATA") || r == "Faulty Data",
        ) != null;
      let isSuccess = false,
        usingSelfReport = false,
        usingPrediction = false,
        isIncomplete = false;

      //Calculate success
      if (isExclusion) {
        // Exclusion, success/fail is meaningless
      } else if (isFaultyData) {
        // Faulty, ignore `succeeded` and check `succeededSelfReported` or `succeededPredicted`
        if (typeof bvr.succeededSelfReported === "boolean") {
          isSuccess = bvr.succeededSelfReported;
          usingSelfReport = true;
        } else if (
          customerPredictionsEnabled &&
          typeof bvr.succeededPredicted === "boolean"
        ) {
          isSuccess = bvr.succeededPredicted;
          usingPrediction = true;
        } else {
          isIncomplete = true;
        }
      } else {
        // Not faulty -> use `succeeded`
        isSuccess = bvr.succeeded;
      }

      //Calculate an overall state and an icon to represent that state
      /** @type {'EXCLUSION'|'INCOMPLETE'|'SUCCESS'|'FAIL'} */
      const state = isExclusion
        ? "EXCLUSION"
        : isIncomplete
          ? "INCOMPLETE"
          : isSuccess
            ? "SUCCESS"
            : "FAIL";
      const stateIcon = {
        EXCLUSION: "ban",
        INCOMPLETE: "question",
        SUCCESS: "check",
        FAIL: "close",
      }[state];

      behavioursWithSuccessObj.push({
        ...bvr,
        _success: {
          isSuccess,
          isExclusion,
          isFaultyData,
          usingSelfReport,
          usingPrediction,
          state,
          stateIcon,
        },
      });
    });
    ev.behaviours = behavioursWithSuccessObj;
  };

  static buildPersonalImpactTotals = (data, selectedYear) => {
    let totals = {
      savings: {
        TOTAL: 0,
        // BEHAVIOR_X: 0 --> will be added dynamically
      },
      donations: {
        TOTAL: 0,
        // BEHAVIOR_X: 0 --> will be added dynamically
      },
    };

    data.forEach((monthlyData) => {
      if (selectedYear && !monthlyData.group.startsWith(`${selectedYear}/`)) {
        return; // ignore any data set that doesnt match the optional year filter
      }
      monthlyData.data.forEach((behaviour) => {
        if (!totals.savings[behaviour.behaviourAcronym]) {
          totals.savings[behaviour.behaviourAcronym] = 0;
        }
        if (!totals.donations[behaviour.behaviourAcronym]) {
          totals.donations[behaviour.behaviourAcronym] = 0;
        }
        totals.savings[behaviour.behaviourAcronym] += behaviour.savingKg;
        totals.savings["TOTAL"] += behaviour.savingKg;
        totals.donations[behaviour.behaviourAcronym] += behaviour.donationUSD;
        totals.donations["TOTAL"] += behaviour.donationUSD;
      });
    });
    const keys = Object.keys(totals.savings);
    keys.forEach((key) => {
      // convert each total from kg of fuel to kg of C02 after they have been totalled
      totals.savings[key] = calculateCO2Convertion(totals.savings[key], 1);
    });
    return totals;
  };

  static buildPersonalImpactTotalsOverview = (data, selectedYear) => {
    let totals = {
      savings: {
        TOTAL: 0,
      },
      donations: {
        TOTAL: 0,
      },
    };

    // Filter operatorYearlyImpact by the selected year if provided
    const yearlyImpact = selectedYear
      ? data.operatorYearlyImpact.filter(
          (yearlyData) => yearlyData.year === selectedYear,
        )
      : data.operatorYearlyImpact;

    // Process the filtered operatorYearlyImpact data
    yearlyImpact.forEach((yearlyData) => {
      yearlyData.impactPerBvr.forEach((impact) => {
        const { behaviourGroupCode, donationsPerYear, fuelSavedPerYear } =
          impact;

        // Initialize behaviour group in totals if not already present
        if (!totals.savings[behaviourGroupCode]) {
          totals.savings[behaviourGroupCode] = 0;
        }
        if (!totals.donations[behaviourGroupCode]) {
          totals.donations[behaviourGroupCode] = 0;
        }

        // Add values to behaviour group and TOTAL
        totals.savings[behaviourGroupCode] += fuelSavedPerYear;
        totals.savings.TOTAL += fuelSavedPerYear;

        totals.donations[behaviourGroupCode] += donationsPerYear;
        totals.donations.TOTAL += donationsPerYear;
      });
    });

    // Convert totals.savings values to strings
    Object.keys(totals.savings).forEach((key) => {
      totals.savings[key] = totals.savings[key].toString();
    });

    return totals;
  };

  static filterArrayUnique = (array) =>
    array.filter((value, index, array) => array.indexOf(value) === index);

  static getRangeStart(delay, pDuration) {
    const reportDelay = delay || 0;
    const lastUpdated = moment.utc().subtract(reportDelay, "days");
    const biweekly = pDuration === "BIWEEKLY";
    let rangeStart = moment.utc(lastUpdated).startOf("month");

    if (biweekly) {
      const dayOfMonth = lastUpdated.date();
      const isFebruary = lastUpdated.month() === 1;

      if (
        (dayOfMonth >= 16 && !isFebruary) ||
        (dayOfMonth >= 15 && isFebruary)
      ) {
        rangeStart.date(isFebruary ? 15 : 16);
      }
    }
    return rangeStart;
  }

  /**
   * Provides a reusable function to format numbers as strings for the specified unit.
   * For single-use or more granular control, use formatMass/formatPercentage/formatNumber from "@signolteam/shared/units-of-measure" instead.
   * @returns {(value: number, uncertainty?: number, options?: import("@signolteam/shared").FormatFunctionOptions) => import("@signolteam/shared").FormatterResult<typeof uncertainty>}
   */

  static getNumberFormatterForUnit(unit) {
    switch (unit.toLowerCase().trim()) {
      case "%": {
        //Assume all percentages use the 0-100 scale instead of 0-1
        return (value, uncertainty, options) =>
          unitsOfMeasure.formatPercentage("0-100", value, uncertainty, options);
      }
      case "kg": {
        return (value, uncertainty, options) =>
          unitsOfMeasure.formatMass(
            SignolUtils.getIndustry(),
            value,
            uncertainty,
            options,
          );
      }
      case "t":
      case "tonne":
      case "tonnes":
      case "mt": {
        return (value, uncertainty, options) =>
          //convert everything back to kg
          unitsOfMeasure.formatMass(
            SignolUtils.getIndustry(),
            value * 1000,
            typeof uncertainty === "number" ? uncertainty * 1000 : uncertainty,
            {
              ...(options || {}),
              boundaryValues:
                options?.boundaryValues?.map((bv) => bv * 1000) ?? [],
            },
          );
      }
      default: {
        return (value, uncertainty, options) =>
          unitsOfMeasure.formatNumber(value, { uncertainty, options });
      }
    }
  }

  /** Provide info for userActivityTracker's click tracking on any child elements.
   * Skips if trackedData is empty, or if you give it `{ actionKey: undefined }`
   */
  static makeTrackableDataAttributes(trackedData) {
    if (!isNotEmptyObject(trackedData)) {
      //non-object, or empty object
      return {};
    }
    if (
      Object.keys(trackedData).length === 1 &&
      Object.prototype.hasOwnProperty.call(trackedData, "actionKey") &&
      trackedData.actionKey == null
    ) {
      //no useful info, eg. { actionKey: undefined }
      return {};
    }
    return { "data-track": JSON.stringify(trackedData) };
  }

  static getBehiveLayoutJSONs(layout) {
    const JSONs = [];
    for (let s = 0; s < layout.sections.length; s++) {
      for (let r = 0; r < layout.sections[s].rows.length; r++) {
        for (let c = 0; c < layout.sections[s].rows[r].cols.length; c++) {
          if (Array.isArray(layout.sections[s].rows[r].cols[c].data)) {
            for (
              let a = 0;
              a < layout.sections[s].rows[r].cols[c].data.length;
              a++
            ) {
              if (layout.sections[s].rows[r].cols[c].data[a]) {
                JSONs.push(layout.sections[s].rows[r].cols[c].data[a]);
              }
            }
          } else {
            JSONs.push(layout.sections[s].rows[r].cols[c].data);
          }
        }
      }
    }
    return JSONs;
  }
}
